<script setup lang="ts">
import type { Contact, ContactAddress } from "@/contacts/types";
import { User, UserCrown, UserStar } from "@iconoir/vue";

defineProps<{
  address: ContactAddress;
  pendingEmailAddress?: string | null;
  pendingPhoneNumber?: string | null;
}>();

const contact = defineModel<Contact>({ required: true });
</script>

<template>
  <div class="multi-columns">
    <div class="column">
      <div>
        <label for="name">Name</label>
        <input
          id="name"
          v-model="contact.name"
          type="text"
          name="name"
          required
        />
      </div>

      <div>
        <label for="salutation">Anrede</label>
        <select v-model="contact.salutation">
          <option :value="null">keine</option>
          <option value="Frau">Frau</option>
          <option value="Herr">Herr</option>
        </select>
      </div>
      <div>
        <label for="street">Straße</label>
        <input
          id="street"
          type="text"
          name="street"
          :value="address.street"
          disabled
        />
      </div>
      <div>
        <label for="houseNumber">Hausnummer</label>
        <input
          id="houseNumber"
          type="text"
          name="houseNumber"
          :value="address.houseNumber"
          disabled
        />
      </div>
      <div>
        <label for="postcode">Postleitzahl</label>
        <input
          id="postcode"
          type="text"
          name="postcode"
          :value="address.postcode"
          disabled
        />
      </div>
      <div>
        <label for="city">Stadt</label>
        <input
          id="city"
          type="text"
          name="city"
          :value="address.city"
          disabled
        />
      </div>

      <div>
        <label for="apartment">Wohnung</label>
        <input
          id="apartment"
          v-model="contact.apartment"
          type="text"
          name="apartment"
        />
      </div>
    </div>

    <div class="column">
      <div>
        <label for="emailAddress"> E-Mail-Adresse </label>
        <input
          id="emailAddress"
          type="email"
          name="emailAddress"
          :value="contact.emailAddress"
          @change="
            contact.emailAddress =
              ($event.target as HTMLInputElement).value.trim() || null
          "
        />
        <small v-if="pendingEmailAddress">
          Änderung zu {{ pendingEmailAddress }} ausstehend
        </small>
      </div>

      <div>
        <label for="phoneNumber"> Telefonnummer </label>
        <input
          id="phoneNumber"
          type="tel"
          pattern="\+?[\d\s]+"
          name="phoneNumber"
          :value="contact.phoneNumber"
          @change="
            contact.phoneNumber =
              ($event.target as HTMLInputElement).value.trim() || null
          "
        />
        <small v-if="pendingPhoneNumber">
          Änderung zu {{ pendingPhoneNumber }} ausstehend
        </small>
      </div>

      <fieldset class="column">
        <div>
          <label for="languages">Sprachen</label>
          <input
            id="languages"
            v-model="contact.languages"
            type="text"
            name="languages"
          />
        </div>

        <div v-if="!$config.disableRenterFields" class="column-self-space-xl">
          <label>
            <input
              v-model="contact.isHouseSpeaker"
              type="checkbox"
              name="isHouseSpeaker"
            />
            <UserCrown color="var(--icon-color-yellow)" />
            Haussprecher:in
          </label>

          <label>
            <input
              v-model="contact.isPotentialCoreActivist"
              type="checkbox"
              name="isPotentialCoreActivist"
            />
            <UserStar color="var(--icon-color-green)" /> potenzielle:r
            Kernaktive:r
          </label>

          <label>
            <input
              v-model="contact.hasNoInterestInBecomingActive"
              type="checkbox"
              name="hasNoInterestInBecomingActive"
            />
            <User color="var(--icon-color-red)" /> Kein Interesse aktiv zu
            werden
          </label>

          <label>
            <input
              v-model="contact.isTenantAssociationMember"
              type="checkbox"
              name="isTenantAssociationMember"
            />
            Mitglied im Mieterschutzverein
          </label>

          <label>
            <input
              v-model="contact.hasLegalProtectionInsurance"
              type="checkbox"
              name="hasLegalProtectionInsurance"
            />
            Hat eine Rechtsschutzversicherung
          </label>
        </div>
      </fieldset>
    </div>
  </div>
</template>
